import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { TrendingDownIcon, BrainIcon, ClockIcon } from 'lucide-react';
interface PainCard {
  stat: string;
  description: string;
  icon: React.ReactNode;
}
const PAIN_CARDS: PainCard[] = [{
  stat: '€2.1M',
  description: 'Average wasted on failed loop iterations per studio annually',
  icon: <TrendingDownIcon className="w-6 h-6" aria-hidden="true" data-id="element-75" />
}, {
  stat: '68%',
  description: 'Of studios still rely on gut feeling for core loop design',
  icon: <BrainIcon className="w-6 h-6" aria-hidden="true" data-id="element-76" />
}, {
  stat: '4 Months',
  description: 'Average delay from concept to viable game loop',
  icon: <ClockIcon className="w-6 h-6" aria-hidden="true" data-id="element-77" />
}];
export function IndustryPain() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  return <section id="problem" ref={sectionRef} className="relative w-full py-32 lg:py-40 overflow-hidden bg-midnight-light" aria-labelledby="pain-heading" data-id="element-78">
      {/* Parallax background accent */}
      <motion.div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-amber/[0.02] blur-[100px]" style={{
      y: bgY
    }} data-id="element-79" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8" data-id="element-80">
        {/* Section header */}
        <motion.div className="text-center mb-20" initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-81">
          <p className="font-sora text-amber text-sm font-semibold tracking-[0.2em] uppercase mb-4" data-id="element-82">
            The Problem
          </p>
          <h2 id="pain-heading" className="font-sora text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" data-id="element-83">
            The Industry's Blind Spot
          </h2>
          <p className="font-inter text-lg text-text-secondary max-w-2xl mx-auto" data-id="element-84">
            Billions flow into iGaming every year, yet the most critical
            component — the core game loop — is still designed by intuition
            alone.
          </p>
        </motion.div>

        {/* Pain cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" data-id="element-85">
          {PAIN_CARDS.map((card, i) => <motion.article key={card.stat} className="relative p-8 lg:p-10 rounded-2xl bg-midnight-lighter/50 backdrop-blur-xl border border-white/5" initial={{
          opacity: 0,
          y: 60
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.7,
          delay: i * 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-86">
              {/* Icon */}
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-amber/10 text-amber mb-6" data-id="element-87">
                {card.icon}
              </div>

              {/* Stat */}
              <p className="font-sora text-4xl lg:text-5xl font-bold text-amber mb-4" data-id="element-88">
                {card.stat}
              </p>

              {/* Description */}
              <p className="font-inter text-text-secondary leading-relaxed" data-id="element-89">
                {card.description}
              </p>

              {/* Subtle top accent line */}
              <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-amber/20 to-transparent" data-id="element-90" />
            </motion.article>)}
        </div>
      </div>
    </section>;
}